// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { dispatchInvestment } from '@advisor-app/features/dashboard/utils/dispatch-investment';
import { useHandlerManageTags } from '@shared/tag-management/hooks/use-handler-manage-tags';
import { ManageTagsModal } from '@shared/tag-management/components/tags-manager';
import { exportTableToExcel } from '@shared/ui-kit/utils/export-table-to-excel';
import { useAppNavigate, useSelectUniverse } from '@advisor-app/hooks';
import { useSendToTrader } from '@advisor-app/features/operations';
import type { Paths } from '@shared/data-access/interfaces/routes/paths';
import { selectInvestmentsTableData } from '@advisor-app/store/slices/broker';
import { setWorkflow } from '@advisor-app/store/slices/workflow';
import { useAppSelector } from '@advisor-app/store/store';
import { TickerTagFeature } from '@shared/data-access/interfaces/api/engine/citec';
import {
  type MyInvestments,
  type SubRows,
} from '@shared/data-access/interfaces/common/my-investment';
import { type WorkflowState } from '@shared/data-access/interfaces/redux/workflow-state';
import {
  AppIcon,
  OptionsSelector,
  SectionContainer,
} from '@shared/ui-kit/components';
import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { formatInvestmentData } from '@shared/ui-kit/utils/format-investment-data';
import { InvestmentTable } from './investment-table';
import { Translation } from '@shared/ui-kit/providers/translation-provider/translation';
import { useTranslation } from 'react-i18next';

export const BrokerMyInvestments = () => {
  const { t } = useTranslation();
  const [openTagsModal, setOpenTagsModal] = useState(false);

  const navigate = useAppNavigate();
  const dispatch = useDispatch();

  const { handleSelectBenchmark } = useSelectUniverse('ai-builder');
  const { sendTradeTicker } = useSendToTrader();

  const investmentData = useAppSelector(selectInvestmentsTableData);

  const {
    onChangeTag,
    selectTag,
    tagNames,
    tags,
    positions,
    tickerTags,
    onCreateTag,
    onCreateTickerTag,
    suggestions,
  } = useHandlerManageTags({
    feature: TickerTagFeature.PortfolioPositions,
    dataSource: investmentData,
  });

  const loadPortfolioOnRedux = useCallback(
    (flow: WorkflowState, data: SubRows[]) => {
      dispatch(setWorkflow(flow));

      dispatchInvestment(selectTag, data);
      // istanbul ignore next
      if (flow.asset === 'stock' || flow.asset === 'etf') {
        handleSelectBenchmark('SPY');
      }

      navigate(
        (flow.type === 'tax-harvesting'
          ? `/tax-manager/${flow.asset}`
          : flow.type === 'rebalance'
          ? `/rebalance`
          : `/analysis/${flow.asset}`) as Paths,
      );
    },
    [dispatch, handleSelectBenchmark, navigate, selectTag],
  );

  const handleOnExportInvestments = useCallback(
    (row: any) => {
      try {
        const filename = `${t('dashboard.my-investments', { ns: 'dashboard' })} - ${
          row.asset_class
        }`;
        const portfolio = row.subRows.map((i: any) => formatInvestmentData(i));
        exportTableToExcel(portfolio, filename);
      } catch (error) {
        // istanbul ignore next
        return error;
      }
    },
    [t],
  );

  const handleOpenModal = useCallback(() => {
    setOpenTagsModal(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setOpenTagsModal(false);
  }, []);

  const defaultText = useMemo(
    () => t('dashboard.select-tag', { ns: 'dashboard' }),
    [t],
  );

  const optionsAvailable = useMemo(
    () => [defaultText, ...tagNames],
    [defaultText, tagNames],
  );

  return (
    <>
      <ManageTagsModal
        onClose={handleCloseModal}
        openModal={openTagsModal}
        tags={tags}
        tickerTags={tickerTags}
        onCreateTag={onCreateTag}
        onCreateTickerTag={onCreateTickerTag}
        suggestions={suggestions}
        dataSource={investmentData}
      />

      <SectionContainer
        title={<Translation i18nKey="glossary.positions" ns="glossary" />}
        popupName="dashboard.portfolioPositions"
        tooltip
        actions={
          <div className="flex items-center space-x-4">
            <div className="w-52">
              <OptionsSelector
                optionsAvailable={optionsAvailable}
                onChangeValueSelected={onChangeTag}
                optionSelected={selectTag}
                defaultText={defaultText}
                selectType="white"
                anchorOrigin={{
                  horizontal: 'center',
                  vertical: 'bottom',
                }}
              />
            </div>

            <button className="flex" onClick={handleOpenModal}>
              <h5 className="pr-2 text-primary-medium">
                <Translation i18nKey="dashboard.manage-tags" ns="dashboard" />
              </h5>
              <AppIcon icon="Lines" size="lg" color="disabled" />
            </button>
          </div>
        }
      >
        <InvestmentTable
          data={positions as MyInvestments[]}
          onLoad={loadPortfolioOnRedux}
          onExport={handleOnExportInvestments}
          onSendToTrader={sendTradeTicker}
        />
      </SectionContainer>
    </>
  );
};
